/**
 * Plain-language copy for auth screens (no Firebase codes or API jargon).
 */

/** Same rules as client/config/firebase.js validatePassword — keep in sync. */
export function getPasswordFeedback(password) {
  if (typeof password !== "string") {
    return "Please enter a password.";
  }

  const missing = [];
  if (password.length < 8) {
    missing.push("at least 8 characters");
  }
  if (!/[A-Z]/.test(password)) {
    missing.push("a capital letter");
  }
  if (!/[a-z]/.test(password)) {
    missing.push("a lowercase letter");
  }
  if (!/[0-9]/.test(password)) {
    missing.push("a number");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    missing.push("a special character (for example !, @, or #)");
  }

  if (missing.length === 0) return null;

  if (missing.length === 1) {
    return `Your password needs ${missing[0]}.`;
  }
  if (missing.length === 2) {
    return `Your password needs ${missing[0]} and ${missing[1]}.`;
  }
  const last = missing.pop();
  return `Your password needs ${missing.join(", ")}, and ${last}.`;
}

/**
 * Turn Firebase errors, fetch/API strings, or thrown messages into user-facing text.
 * @param {unknown} error — FirebaseError, Error, or string
 * @param {"login" | "signup"} context
 */
export function authErrorForUser(error, context = "login") {
  if (error == null) {
    return "Something went wrong. Please try again.";
  }

  const code = typeof error === "object" && error && "code" in error ? error.code : null;
  const raw =
    typeof error === "string"
      ? error
      : error && typeof error.message === "string"
        ? error.message
        : String(error);

  const lower = raw.toLowerCase();

  // --- Firebase auth codes (reliable) ---
  if (code === "auth/invalid-credential" || code === "auth/wrong-password") {
    return "That email and password do not match. Check for typos, or use “Forgot password?” below.";
  }
  if (code === "auth/user-not-found") {
    return "We could not find an account with that email. You may need to sign up first.";
  }
  if (code === "auth/invalid-email") {
    return "That does not look like a valid email. Please check it and try again.";
  }
  if (code === "auth/email-already-in-use") {
    return "That email is already in use. Try signing in, or use a different email.";
  }
  if (code === "auth/weak-password" || code === "auth/password-does-not-meet-requirements") {
    return "That password is too easy to guess. Please choose a stronger one.";
  }
  if (code === "auth/user-disabled") {
    return "This account is no longer active. Please contact support if you need help.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many tries. Wait a few minutes, then try again.";
  }
  if (code === "auth/network-request-failed") {
    return "We could not reach the server. Check your connection and try again.";
  }

  // --- Substrings (when code is missing or message-only) ---
  if (lower.includes("invalid-credential")) {
    return "That email and password do not match. Check for typos, or use “Forgot password?” below.";
  }
  if (lower.includes("wrong-password")) {
    return "That email and password do not match. Check for typos, or use “Forgot password?” below.";
  }
  if (lower.includes("user not found") || lower.includes("user-not-found")) {
    return "We could not find an account with that email. You may need to sign up first.";
  }
  if (lower.includes("invalid-email")) {
    return "That does not look like a valid email. Please check it and try again.";
  }
  if (lower.includes("email-already-in-use") || lower.includes("already in use")) {
    return "That email is already in use. Try signing in, or use a different email.";
  }
  if (lower.includes("weak-password") || lower.includes("password should be at least")) {
    return "That password is too easy to guess. Please choose a stronger one.";
  }
  if (lower.includes("too-many-requests")) {
    return "Too many tries. Wait a few minutes, then try again.";
  }
  if (lower.includes("network")) {
    return "We could not reach the server. Check your connection and try again.";
  }

  // --- Our app / API strings ---
  if (
    lower.includes("verify your email") ||
    lower.includes("email verification required") ||
    lower.includes("not verified")
  ) {
    return "Please confirm your email first. Check your inbox for the link we sent you.";
  }
  if (lower.includes("user not found") && lower.includes("registration")) {
    return "Your account is not finished setting up. Try signing up again, or contact support.";
  }
  if (lower.includes("token") && (lower.includes("invalid") || lower.includes("expired"))) {
    return "Your session expired. Please sign in again.";
  }
  if (lower.includes("token does not match")) {
    return "Something went wrong while signing you in. Please try again.";
  }
  if (lower.includes("no user signed in")) {
    return "We could not send the email because you are not signed in. Close this message and try signing in again.";
  }
  if (lower.includes("missing required")) {
    return "Please fill in every field.";
  }
  if (lower.includes("invalid role")) {
    return "That account type is not valid here. Go back and pick Bar Owner or Artist.";
  }
  if (lower.includes("user already exists")) {
    return "That email is already in use. Try signing in instead.";
  }

  // From getPasswordFeedback (already plain language)
  if (
    lower.startsWith("your password needs") ||
    lower.startsWith("please enter a password")
  ) {
    return raw;
  }

  // Fallback: signup vs login wording
  if (context === "signup") {
    return "We could not create your account. Please try again, or contact support if this keeps happening.";
  }
  return "We could not sign you in. Please try again, or contact support if this keeps happening.";
}
